import { motion } from "framer-motion";
import Image from "next/image";
import { useState, type FC } from "react";
import { IoArrowBack } from "react-icons/io5";

import { ImageSwipeDots } from "../ImageSwipeDots";
import { Button } from "tw/components/atoms/Button";

export interface OnboardingBodyProps {
  steps: {
    highlightedTitle?: string;
    title: string;
    description: string;
    videoLink: string;
  }[];
  closeSettingsPopup: () => void;
}

export const OnboardingBody: FC<OnboardingBodyProps> = ({
  steps,
  closeSettingsPopup,
}) => {
  const [stepIndex, setStepIndex] = useState(0);

  const isLastStep = steps.length - 1 === stepIndex;
  const { highlightedTitle, title, description, videoLink } = steps[stepIndex];

  return (
    <div className="px-2 md:px-16 md:pb-4">
      <div className="text-center font-semibold text-2xl md:text-3xl lg:text-4xl mt-12 md:mt-6">
        <span className="block text-accent">{highlightedTitle}</span>
        {title}
      </div>
      <p className="mt-3 text-secondary-text text-center text-sm leading-4 md:text-base md:leading-5 lg:text-lg lg:leading-6">
        {description}
      </p>
      <div className="relative w-full mt-8 flex items-center">
        {steps?.[stepIndex - 1]?.videoLink && (
          <div className="absolute w-[75%] -translate-x-[107%] opacity-20">
            <Image
              src={steps[stepIndex - 1].videoLink}
              className="rounded-2xl"
              alt="Previous step"
              width={1132}
              height={720}
            />
          </div>
        )}
        <div className="relative w-full">
          <Image
            src={videoLink}
            className="rounded-2xl transition ease-in-out duration-500"
            alt="Current step"
            width={1132}
            height={720}
          />
        </div>
        {steps?.[stepIndex + 1]?.videoLink && (
          <div className="hidden md:flex absolute w-[75%] translate-x-[140%] opacity-20">
            <Image
              src={steps[stepIndex + 1].videoLink}
              className="rounded-2xl"
              alt="Next step"
              width={1132}
              height={720}
            />
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-9">
        <div className="flex md:w-[250px]">
          {stepIndex === 0 ? (
            <motion.button
              whileTap={{ scale: 0.95, animationDuration: "0.2s" }}
              onClick={closeSettingsPopup}
              className="text-secondary-text text-xl font-semibold"
            >
              Skip tutorial
            </motion.button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setStepIndex((cur) => cur - 1);
                }}
                label="Back"
                className="hidden md:flex"
              />
              <Button
                variant="secondary"
                leftIcon={<IoArrowBack className="w-8 h-8 min-w-[32px]" />}
                onClick={() => {
                  setStepIndex((cur) => cur - 1);
                }}
                className="flex md:hidden max-w-[48px] px-0 w-[48px]"
              />
            </>
          )}
        </div>
        <ImageSwipeDots
          current={stepIndex}
          total={steps.length}
          setCurrent={setStepIndex}
          className="hidden md:flex"
        />
        <div className="flex md:w-[250px] justify-end">
          <Button
            variant="primary"
            label={isLastStep ? "Review SOL staking" : "Next"}
            onClick={
              isLastStep
                ? closeSettingsPopup
                : () => {
                    setStepIndex((cur) => cur + 1);
                  }
            }
          />
        </div>
      </div>
    </div>
  );
};
